import cloneDeep from 'lodash/cloneDeep'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import lodashIsPlainObject from 'lodash/isPlainObject'
import merge from 'lodash/merge'
import { RolodexEntryPatchJson } from 'types'

type DiffResult = {
  data: Record<string, unknown>
  deletePaths: ReadonlyArray<string>
}

/**
 * Create a Rolodex patch to transform the entry data field from the contents of 'from' to the contents of 'to'.
 * @param id - The ID of the entry to patch.
 * @param from - The contents of the entry data field before the patch.
 * @param to - The contents of the entry data field after the patch.
 * @returns A Rolodex patch to transform the entry data field from the contents of 'from' to the contents of 'to', or undefined if no changes are needed.
 * @example
 * const patch = createPatchData('123', { foo: 'bar' }, { foo: 'baz' })
 * console.log(patch)
 * // { id: '123', data: { foo: 'baz' } }
 *
 * const patch = createPatchData('123', { foo: 'bar' }, { foo: 'bar' })
 * console.log(patch)
 * // undefined
 */
export function createPatchData(
  id: string,
  from: Readonly<Record<string, unknown>> = {},
  to: Readonly<Record<string, unknown>> = {}
): RolodexEntryPatchJson | undefined {
  const { data, deletePaths } = diffFields([], from, to)
  const patch: RolodexEntryPatchJson = { id }

  if (!isEmpty(data)) {
    patch.data = data
  }

  if (deletePaths.length) {
    patch.delete_data = Array.from(new Set(deletePaths.filter(Boolean)))
  }

  if (
    !patch.data &&
    (!patch.delete_data || patch.delete_data.length === 0) &&
    !patch.type &&
    !patch.tags
  ) {
    return undefined
  }

  return patch
}

function diffFields(
  path: ReadonlyArray<string>,
  fromValue: unknown,
  toValue: unknown
): DiffResult {
  const fromIsObject = isPlainObject(fromValue)
  const toIsObject = isPlainObject(toValue)

  if (fromIsObject && toIsObject) {
    const fromRecord = fromValue as Readonly<Record<string, unknown>>
    const toRecord = toValue as Readonly<Record<string, unknown>>
    const keys = new Set([...Object.keys(fromRecord), ...Object.keys(toRecord)])

    let aggregateData: Record<string, unknown> = {}
    let aggregateDeletes: string[] = []

    keys.forEach((key) => {
      const nextPath = [...path, key]
      const hasFrom = Object.prototype.hasOwnProperty.call(fromRecord, key)
      const hasTo = Object.prototype.hasOwnProperty.call(toRecord, key)

      if (!hasTo || toRecord[key] === undefined) {
        aggregateDeletes.push(joinPath(nextPath))
        return
      }

      if (!hasFrom) {
        aggregateData = mergeData(
          aggregateData,
          createNestedPatch(nextPath, toRecord[key])
        )
        return
      }

      const { data, deletePaths } = diffFields(
        nextPath,
        fromRecord[key],
        toRecord[key]
      )
      aggregateData = mergeData(aggregateData, data)
      aggregateDeletes = aggregateDeletes.concat(deletePaths)
    })

    return { data: aggregateData, deletePaths: aggregateDeletes }
  }

  if (toValue === undefined) {
    return { data: {}, deletePaths: [joinPath(path)] }
  }

  if (isEqual(fromValue, toValue)) {
    return { data: {}, deletePaths: [] }
  }

  return { data: createNestedPatch(path, toValue), deletePaths: [] }
}

function mergeData(
  target: Record<string, unknown>,
  source: Record<string, unknown>
): Record<string, unknown> {
  if (isEmpty(source)) {
    return target
  }
  if (isEmpty(target)) {
    return cloneDeep(source)
  }
  return merge(target, cloneDeep(source))
}

function createNestedPatch(
  path: ReadonlyArray<string>,
  value: unknown
): Record<string, unknown> {
  if (value === undefined) {
    return {}
  }

  if (!path.length) {
    if (!isPlainObject(value)) {
      throw new Error('Root-level patch values must be objects')
    }
    return cloneDeep(value as Record<string, unknown>)
  }

  const [head, ...rest] = path
  if (!rest.length) {
    return { [head]: cloneDeep(value) }
  }

  return {
    [head]: createNestedPatch(rest, value),
  }
}

function joinPath(path: ReadonlyArray<string>): string {
  return path.join('.')
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return lodashIsPlainObject(value)
}
